import { CSSProperties, useContext, useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import AuthContext from 'contexts/AuthContext';
import { useToastContext } from 'components/alert/ToastContext';
import { Label } from 'components/input/Label';
import { cadastrosFiscais } from 'model/fiscal/CadastrosFiscais';
import { CampoCadastroFiscal, OpcaoFiscal, RegistroFiscal } from 'model/fiscal/ParametrosFiscais';
import ParametrosFiscaisService from 'service/fiscal/ParametrosFiscaisService';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

const service = new ParametrosFiscaisService();
function mensagem(error: unknown): string {
    if (isAxiosError<{ message?: string; detail?: string }>(error)) return error.response?.data?.detail || error.response?.data?.message || 'Não foi possível concluir a operação.';
    return 'Não foi possível concluir a operação.';
}

export default function ParametrosFiscaisCadastro() {
    const { acoes } = useContext(AuthContext);
    const { msgSucesso, msgErro, msgAlerta } = useToastContext();
    const podeConsultar = acoes?.some(a => a.sigla === 'PARAMETRO_FISCAL_CONSULTAR');
    const podeGerenciar = acoes?.some(a => a.sigla === 'PARAMETRO_FISCAL_GERENCIAR');
    const [cadastro, setCadastro] = useState(cadastrosFiscais[0]);
    const [lista, setLista] = useState<RegistroFiscal[]>([]);
    const [registro, setRegistro] = useState<RegistroFiscal | null>(null);
    const [opcoes, setOpcoes] = useState<Record<string, OpcaoFiscal[]>>({});
    const [ocupado, setOcupado] = useState(false);
    const [carregando, setCarregando] = useState(false);

    useEffect(() => {
        if (!podeConsultar) {
            if (acoes) msgAlerta('Atenção', 'Sem permissão para consultar parâmetros fiscais.');
            return;
        }
        let ativo = true;
        setLista([]); setRegistro(null); setCarregando(true);
        const refs = Array.from(new Set(cadastro.campos.map(c => c.referencia).filter((r): r is string => !!r)));
        Promise.all([service.listar(cadastro.nome), Promise.all(refs.map(async r => [r, await service.opcoes(r)] as const))])
            .then(([dados, referencias]) => { if (ativo) { setLista(dados); setOpcoes(Object.fromEntries(referencias)); } })
            .catch(e => { if (ativo) msgErro('Erro', mensagem(e)); })
            .finally(() => { if (ativo) setCarregando(false); });
        return () => { ativo = false; };
    }, [cadastro, podeConsultar, acoes, msgErro, msgAlerta]);

    async function salvar() {
        if (!registro || ocupado || !podeGerenciar) return;
        const faltantes = cadastro.campos.filter(c => c.obrigatorio && (registro[c.nome] === null || registro[c.nome] === undefined || registro[c.nome] === ''));
        if (faltantes.length) {
            msgAlerta('Atenção', `Informe: ${faltantes.map(c => c.label).join(', ')}.`);
            return;
        }
        setOcupado(true);
        try {
            await service.salvar(cadastro.nome, registro);
            setLista(await service.listar(cadastro.nome));
            setRegistro(null);
            msgSucesso('Sucesso', 'Cadastro salvo.');
        } catch (e) {
            msgErro('Erro', mensagem(e));
        } finally {
            setOcupado(false);
        }
    }
    function novo() {
        const inicial: RegistroFiscal = { ativo: true };
        cadastro.campos.filter(c => c.tipo === 'boolean' && c.nome !== 'ativo').forEach(c => { inicial[c.nome] = false; });
        setRegistro(inicial);
    }
    function excluir(r: RegistroFiscal) {
        if (!r.id || !podeGerenciar) return;
        confirmDialog({
            header: 'Excluir cadastro', message: 'Excluir este cadastro fiscal? Cadastros utilizados em documentos não poderão ser excluídos.',
            acceptLabel: 'Excluir', rejectLabel: 'Voltar', accept: async () => {
                setOcupado(true);
                try {
                    await service.excluir(cadastro.nome, r.id!);
                    setLista(await service.listar(cadastro.nome));
                    msgSucesso('Sucesso', 'Cadastro excluído.');
                } catch (e) {
                    msgErro('Erro', mensagem(e));
                } finally {
                    setOcupado(false);
                }
            }
        });
    }
    function mostrar(c: CampoCadastroFiscal, r: RegistroFiscal): string {
        const value = r[c.nome];
        if (value === null || value === undefined) return '—';
        if (c.tipo === 'boolean') return value ? 'Sim' : 'Não';
        if (c.tipo === 'referencia') return (c.opcoes || opcoes[c.referencia || ''] || []).find(o => o.value === value)?.label || String(value);
        if (c.tipo === 'numero' && typeof value === 'number') return value.toLocaleString('pt-BR', { maximumFractionDigits: 6 });
        return String(value);
    }
    function campo(c: CampoCadastroFiscal) {
        if (!registro) return null;
        const value = registro[c.nome];
        const alterar = (v: string | number | boolean | null) => setRegistro({ ...registro, [c.nome]: v });
        const id = `fiscal-${c.nome}`;
        if (c.tipo === 'boolean') return <Checkbox inputId={id} checked={value === true} className='mb-2' onChange={e => alterar(!!e.checked)} disabled={ocupado} />;
        if (c.tipo === 'referencia') return <Dropdown inputId={id} value={value ?? null} options={c.opcoes || opcoes[c.referencia || ''] || []} onChange={e => alterar(e.value ?? null)} filter showClear={!c.obrigatorio} placeholder="Selecione" style={{ width: '100%' }} disabled={ocupado} />;
        if (c.tipo === 'numero') return <InputNumber inputId={id} value={typeof value === 'number' ? value : null} onValueChange={e => alterar(e.value ?? null)} locale="pt-BR" min={0} max={c.percentual ? 100 : undefined} maxFractionDigits={6} style={{ width: '100%' }} disabled={ocupado} />;
        return <InputText id={id} type={c.tipo === 'data' ? 'date' : 'text'} value={typeof value === 'string' ? value : ''} onChange={e => alterar(e.target.value || null)} maxLength={c.tamanho} style={{ width: '100%' }} disabled={ocupado} />;
    }
    if (!podeConsultar) return null;
    const colunas = cadastro.campos.filter(c => c.tipo !== 'boolean' || c.nome === 'ativo').slice(0, 5);
    const camposModal = [
        ...cadastro.campos.filter(c => c.nome === 'ativo'),
        ...cadastro.campos.filter(c => !['ativo', 'inicioVigencia', 'fimVigencia'].includes(c.nome)),
        ...cadastro.campos.filter(c => c.nome === 'inicioVigencia'),
        ...cadastro.campos.filter(c => c.nome === 'fimVigencia')
    ];
    return <div className='card' style={{ padding: 12 }}>
        <ConfirmDialog />
        <h2>Parâmetros fiscais</h2>
        <p>Configure os tratamentos por transação, produto e estado. As configurações são compartilhadas entre homologação e produção.</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Dropdown value={cadastro} options={cadastrosFiscais} optionLabel="label" onChange={e => setCadastro(e.value)} filter className="w-full md:w-6" disabled={ocupado} />
            {podeGerenciar && <Button label="Novo" icon="pi pi-plus" onClick={novo} disabled={carregando || ocupado} />}
            <Button type="button" label="Documentação" icon="pi pi-book" outlined
                onClick={() => window.open('https://empxdocs.appsotecnologia.com.br/fiscal/cadastros/parametros-fiscais/geral', '_blank', 'noopener,noreferrer')} />
        </div>
        <DataTable value={lista} loading={carregando} dataKey="id" paginator rows={15} emptyMessage="Nenhum cadastro encontrado.">
            {colunas.map(c => <Column key={c.nome} header={c.label} body={(r: RegistroFiscal) => mostrar(c, r)} />)}
            {podeGerenciar && <Column header="Ações" body={(r: RegistroFiscal) => (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <Button icon="pi pi-pencil" aria-label="Editar" onClick={() => setRegistro({ ...r })} disabled={ocupado} />
                    <Button icon="pi pi-trash" aria-label="Excluir" severity="danger" onClick={() => excluir(r)} disabled={ocupado} />
                </div>)} />}
        </DataTable>
        <Dialog visible={!!registro} header={`${registro?.id ? 'Editar' : 'Novo'} — ${cadastro.label}`} onHide={() => { if (!ocupado) setRegistro(null); }} style={{ width: 'min(950px, 95vw)' }} closable={!ocupado} footer={<Button label="Salvar" icon="pi pi-check" loading={ocupado} onClick={salvar} />}>
            {cadastro.nome === 'ibsCbsAliquota' && <p className="text-muted">
                Deixe estado e município em branco para cadastrar uma alíquota geral do tributo.
                Configurações específicas compatíveis têm prioridade sobre a geral.
            </p>}
            <div className="row">
                {camposModal.map(c => {
                    let className = 'col-12 mb-3';
                    if (c.nome === 'inicioVigencia' || c.nome === 'fimVigencia') className = 'col-6 mb-3';

                    let style: CSSProperties = {};
                    if (c.tipo === 'boolean') style = { display: 'flex', flexDirection: "row-reverse", alignItems: 'center', justifyContent: "start", gap: 8 };

                    return (
                        <div className={className} style={style} key={c.nome}>
                            <Label htmlFor={`fiscal-${c.nome}`} className="d-block mb-2" obrigatorio={c.obrigatorio}>
                                {c.label}
                            </Label>
                            {campo(c)}
                        </div>
                    )
                })}
            </div>
        </Dialog>
    </div>;
}
